import { useMemo, useState } from 'react';

import { isMobile } from 'hooks';
import { useAppSelector } from 'store';
import { Tabs } from 'components/tabs';
import { formatDate } from 'utils/date';
import { Drawer } from 'components/drawer';
import { Loader } from 'components/loader';
import { joinStrings } from 'utils/helpers';
import { Flex, FlexProps } from 'components/flex';
import { BackOfficeRoles, EventSummaryReportTab } from 'types';
import { useReportQuery, useDetailsQuery } from 'store/api/event';
import { EventReportHeader } from 'components/event-report-header';
import {
  hasUserRole,
  isCompanyOwnerOrSiteAdmin,
  selectCanReadMissingSales,
} from 'store/slices/auth';

import * as Styles from './styles';
import { Revenue } from './revenue';
import { Refunds } from './refunds';
import { Bonuses } from './bonuses';
import { Expenses } from './expenses';
import { CashFlow } from './cash-flow';
// import { Received } from './received';
import { Inventory } from './inventory';
import { Dashboard } from './dashboard';
import { Commissions } from './commissions';
import { StockOrders } from './stock-orders';
import { MissingSales } from './missing-sales';
import { Reimbursement } from './reimbursement';
import { ProfitAndLosses } from './profit-and-losses';
import { TabTitles, EventReportProps } from './types';

const commonFlexProps: FlexProps = {
  gap: 24,
  minHeight: 0,
  fullHeight: true,
  flexDirection: 'column',
};

const displayHiddenTabsRoles = [
  BackOfficeRoles.ACCOUNTING,
  BackOfficeRoles.ADMINISTRATIVE,
];
const hiddenTabs = [
  EventSummaryReportTab.MissingSales,
  EventSummaryReportTab.ProfitAndLosses,
  EventSummaryReportTab.StockOrders,
  EventSummaryReportTab.Commissions,
];

export const EventReportDialog = ({
  event,
  opened,
  onClose,
  openedTab = EventSummaryReportTab.Dashboard,
}: EventReportProps) => {
  const mobile = isMobile();
  const [tab, setTab] = useState<EventSummaryReportTab>(openedTab);
  const canReadMissing = useAppSelector(selectCanReadMissingSales);
  const visibleTab =
    tab === EventSummaryReportTab.MissingSales && !canReadMissing
      ? EventSummaryReportTab.Dashboard
      : tab;
  const isAdminRoles = useAppSelector(isCompanyOwnerOrSiteAdmin);
  const isAdministrativeRoles = useAppSelector(
    hasUserRole(displayHiddenTabsRoles)
  );

  const { data, isLoading } = useReportQuery(
    {
      id: String(event?.id),
    },
    { skip: !event?.id }
  );

  // The passed event can carry clamped dates (the P&L list ends it today), so
  // the header and the Dashboard use the event record itself when available.
  // The other tabs keep the event they were given.
  const { data: eventDetails, isLoading: isEventLoading } = useDetailsQuery(
    String(event?.id),
    { skip: !event?.id }
  );
  const reportEvent = eventDetails ?? event;

  const tabs = useMemo(
    () =>
      Object.values(EventSummaryReportTab)
        .filter((value) => {
          if (value === EventSummaryReportTab.MissingSales) {
            return canReadMissing;
          }
          if (hiddenTabs.includes(value)) {
            return isAdministrativeRoles || isAdminRoles;
          }
          return true;
        })
        .map((value) => ({ to: value, label: TabTitles[value] })),
    [isAdministrativeRoles, isAdminRoles, canReadMissing]
  );

  const commonProps = useMemo(
    () => ({ event, data: data?.report || [] }),
    [data?.report, event]
  );

  const renderTab = useMemo(
    () => ({
      [EventSummaryReportTab.Bonuses]: <Bonuses event={event} />,
      [EventSummaryReportTab.Refunds]: <Refunds event={event} />,
      [EventSummaryReportTab.Expenses]: <Expenses {...commonProps} />,
      [EventSummaryReportTab.CashFlow]: <CashFlow {...commonProps} />,
      // TODO: uncomment when it was ready
      // [EventSummaryReportTab.Received]: <Received {...commonProps} />,
      [EventSummaryReportTab.Inventory]: <Inventory {...commonProps} />,
      [EventSummaryReportTab.Commissions]: <Commissions event={event} />,
      [EventSummaryReportTab.StockOrders]: <StockOrders event={event} />,
      [EventSummaryReportTab.Reimbursements]: <Reimbursement event={event} />,
      [EventSummaryReportTab.Revenue]: <Revenue {...commonProps} data={data} />,
      [EventSummaryReportTab.MissingSales]: (
        <MissingSales event={reportEvent} />
      ),
      [EventSummaryReportTab.Dashboard]: (
        <Dashboard event={reportEvent} id={String(event?.id)} />
      ),
      [EventSummaryReportTab.ProfitAndLosses]: (
        <ProfitAndLosses
          {...commonProps}
          onMissingSales={() => setTab(EventSummaryReportTab.MissingSales)}
        />
      ),
    }),
    [event, reportEvent, commonProps, data]
  );

  return (
    <Drawer opened={opened} topOffset={mobile ? '10%' : '15%'}>
      <Loader isFullScreen={false} isLoading={isLoading || isEventLoading}>
        {data && (
          <Flex {...commonFlexProps}>
            <EventReportHeader
              onClose={onClose}
              event={reportEvent}
              title="Event Summary Report"
              isEmptyReport={
                !data?.report?.length && !data?.missingSales?.count
              }
              date={joinStrings(
                [
                  formatDate(reportEvent.startDate),
                  formatDate(reportEvent.endDate),
                ],
                ' - '
              )}
            />

            <Flex {...commonFlexProps}>
              <Tabs
                items={tabs}
                value={visibleTab}
                onChange={(value) => setTab(value as EventSummaryReportTab)}
              />
              <Styles.TabContainer>{renderTab[visibleTab]}</Styles.TabContainer>
            </Flex>
          </Flex>
        )}
      </Loader>
    </Drawer>
  );
};
